import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from 'react-i18next';

// NOTE: The compiler will fail if 2020_FortuneTalks-723-scaled.jpg is missing in the Assets folder.
// We are using the existing Master-Profile-Siewli.png as a safe fallback.
// Once you add '2020_FortuneTalks-723-scaled.jpg' to the src/Components/Assets directory,
// you can change the import below.
import ownerImg from './Assets/mastersiewli.png';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: transparent;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ isMobile }) => (isMobile ? '40px 20px' : '80px 40px')};
  font-family: "DMSans", sans-serif;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeroSection = styled.div`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  align-items: center;
  gap: 50px;
  width: 100%;
  margin-bottom: 60px;
`;

const ImageContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const ProfileImg = styled.img`
  width: 60%;
  max-width: 600px;
  height: auto;
  object-fit: contain;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(222, 175, 119, 0.25);
  border: 2px solid #deaf77;
`;

const HeroInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  text-align: ${({ isMobile }) => (isMobile ? 'center' : 'left')};
`;

const Title = styled.h1`
  font-family: "Playfair Display", Georgia, serif;
  color: #deaf77;
  font-size: ${({ isMobile }) => (isMobile ? '2.5rem' : '3.5rem')};
  margin: 0;
  line-height: 1.2;
`;

const Subtitle = styled.h2`
  font-family: "DMSans-Bold", sans-serif;
  color: #c9a84b;
  font-size: ${({ isMobile }) => (isMobile ? '1.2rem' : '1.4rem')};
  margin: 15px 0 25px 0;
`;

const Quote = styled.p`
  font-family: "DMSans", sans-serif;
  font-style: italic;
  font-size: ${({ isMobile }) => (isMobile ? '1.1rem' : '1.3rem')};
  color: #FFF2D2;
  border-left: ${({ isMobile }) => (isMobile ? 'none' : '4px solid #deaf77')};
  padding-left: ${({ isMobile }) => (isMobile ? '0' : '20px')};
  margin: 0 0 30px 0;
  line-height: 1.6;
`;

const Badge = styled.div`
  display: inline-block;
  background-color: #deaf77;
  color: #0d0d0d;
  font-family: "DMSans-Bold", sans-serif;
  padding: 12px 24px;
  border-radius: 30px;
  font-size: 1rem;
  align-self: ${({ isMobile }) => (isMobile ? 'center' : 'flex-start')};
  box-shadow: 0 4px 15px rgba(222, 175, 119, 0.3);
`;

const BiographySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  width: 100%;
  background-color: transparent;
  padding: ${({ isMobile }) => (isMobile ? '30px 0' : '50px 0')};
`;

const BioParagraph = styled.p`
  font-family: "DMSans", sans-serif;
  font-size: 1.05rem;
  line-height: 1.8;
  color: #e0e0e0;
  margin: 0;
  text-align: justify;
`;

const AboutPage = () => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  return (
    <PageContainer isMobile={isMobile}>
      <ContentWrapper>
        <HeroSection isMobile={isMobile}>
          <ImageContainer>
            <ProfileImg src={ownerImg} alt={t("aboutPage.imgAlt")} />
          </ImageContainer>
          <HeroInfo isMobile={isMobile}>
            <Title isMobile={isMobile}>{t("aboutPage.name")}</Title>
            <Subtitle isMobile={isMobile}>{t("aboutPage.subtitle")}</Subtitle>
            <Quote isMobile={isMobile}>{t("aboutPage.quote")}</Quote>
            <Badge isMobile={isMobile}>{t("aboutPage.badge")}</Badge>
          </HeroInfo>
        </HeroSection>

        <BiographySection isMobile={isMobile}>
          <BioParagraph>{t("aboutPage.bio1")}</BioParagraph>
          <BioParagraph>{t("aboutPage.bio2")}</BioParagraph>
          <BioParagraph>{t("aboutPage.bio3")}</BioParagraph>
          <BioParagraph>{t("aboutPage.bio4")}</BioParagraph>
          <BioParagraph>{t("aboutPage.bio5")}</BioParagraph>
          <BioParagraph>{t("aboutPage.bio6")}</BioParagraph>
          <BioParagraph>{t("aboutPage.bio7")}</BioParagraph>
        </BiographySection>
      </ContentWrapper>
    </PageContainer>
  );
};

export default AboutPage;
